// Lightweight frontend logger utility to send logs to mcp-gateway
// Path: services/frontend/src/utils/logger.js

// Use absolute API base to avoid dev proxy dependence and reduce CORS surprises
// [중요] 운영 환경에서 REACT_APP_API_URL이 누락되면 localhost로 호출되어 로그인/로그 전송이 실패할 수 있습니다.
//        따라서 기본값은 동일 오리진의 상대 경로(빈 문자열)로 두어 `/api/...`가 현재 도메인으로 호출되도록 합니다.
const API_BASE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
  ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '')
  : '';
const ENDPOINT = `${API_BASE}/api/logs/client`;

// --- Sensitive data sanitization ---
const SENSITIVE_KEY_RE = /^(password|pass|pwd|confirmPassword|newPassword|currentPassword|secret|token)$/i;

function sanitizeString(str) {
  if (typeof str !== 'string') return str;
  let s = str;
  try {
    // Mask query-like segments: password=xxxxx -> password=********
    s = s.replace(/(password|pass|pwd|confirmPassword|newPassword|currentPassword)\s*=\s*([^&\s;]+)(?=\s|&|;|$)/gi, '$1=********');
    // If an input password DOM snippet is included, remove or mask value="..."
    // Example: <input type="password" ... value="secret"> -> value="********"
    s = s.replace(/(<input[^>]*type\s*=\s*"password"[^>]*\bvalue\s*=\s*")([^"]*)("[^>]*>)/gi, '$1********$3');
    // Generic HTML attribute value masking for value="..." when near 'password'
    s = s.replace(/(password[^>]{0,200}?\bvalue\s*=\s*")([^"]*)(")/gi, '$1********$3');
  } catch (_) {}
  return s;
}

function sanitizeValue(val, depth = 0) {
  if (depth > 3) return '[Truncated]';
  if (val == null) return val;
  const t = typeof val;
  if (t === 'string') return sanitizeString(val);
  if (t === 'number' || t === 'boolean') return val;
  if (Array.isArray(val)) return val.map((v) => sanitizeValue(v, depth + 1));
  if (t === 'object') {
    const out = {};
    for (const k in val) {
      if (!Object.prototype.hasOwnProperty.call(val, k)) continue;
      if (SENSITIVE_KEY_RE.test(k)) {
        out[k] = '********';
      } else {
        out[k] = sanitizeValue(val[k], depth + 1);
      }
    }
    return out;
  }
  try { return String(val); } catch { return undefined; }
}

function sanitizePayload(payload) {
  try {
    const safe = sanitizeValue(payload, 0);
    return safe;
  } catch (_) {
    return payload;
  }
}

function uuidv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch (e) {}
  }
  // Fallback UUID generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

function getOrCreateRequestId() {
  try {
    const key = 'x-request-id';
    let rid = sessionStorage.getItem(key);
    if (!rid) {
      rid = uuidv4();
      sessionStorage.setItem(key, rid);
    }
    return rid;
  } catch (_) {
    return uuidv4();
  }
}

export function setRequestId(id) {
  try { sessionStorage.setItem('x-request-id', id); } catch (_) {}
}

export async function sendClientLog(level, message, context) {
  const payload = {
    level: String(level || 'info'),
    message: sanitizeString(String(message || '')),
    context: sanitizeValue(context || undefined),
    url: (typeof window !== 'undefined' && window.location) ? window.location.href : undefined,
    ts: new Date().toISOString()
  };

  const headers = { 'Content-Type': 'application/json' };
  const rid = getOrCreateRequestId();
  if (rid) headers['X-Request-Id'] = rid;

  try {
    // Add a short timeout so logging never blocks UX
    const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 5000) : null;
    const safePayload = sanitizePayload(payload);
    await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(safePayload),
      // keepalive allows the request to outlive page unload in some browsers
      keepalive: true,
      // Do not send credentials to avoid CORS restrictions across environments
      credentials: 'omit',
      mode: 'cors',
      signal: controller ? controller.signal : undefined,
    });
    if (timeoutId) clearTimeout(timeoutId);
  } catch (_) {
    // Swallow errors to avoid affecting UX
  }
}

export function setupGlobalErrorLogging() {
  if (typeof window === 'undefined') return;

  // Avoid double binding
  if (window.__client_log_bound) return;
  window.__client_log_bound = true;

  window.addEventListener('error', (e) => {
    try {
      sendClientLog('error', e?.message || 'window.error', {
        source: e?.filename,
        lineno: e?.lineno,
        colno: e?.colno,
        error: e?.error ? String(e.error?.stack || e.error) : undefined
      });
    } catch (_) {}
  });

  window.addEventListener('unhandledrejection', (e) => {
    try {
      const reason = e?.reason;
      sendClientLog('error', 'unhandledrejection', {
        reason: typeof reason === 'string' ? reason : String(reason?.stack || reason)
      });
    } catch (_) {}
  });
}

// Optional convenience wrappers
export const log = {
  debug: (m, c) => sendClientLog('debug', m, c),
  info: (m, c) => sendClientLog('info', m, c),
  warn: (m, c) => sendClientLog('warning', m, c),
  error: (m, c) => sendClientLog('error', m, c)
};
